import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
  EyeIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  Squares2X2Icon,
  BriefcaseIcon,
  SwatchIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  agregarCristal,
  obtenerCristales,
  editarCristal,
  eliminarCristal,
  agregarLenteContacto,
  obtenerLentesContacto,
  editarLenteContacto,
  eliminarLenteContacto,
  obtenerCatalogosCristales,
  crearCatalogoCristalesItem,
  actualizarCatalogoCristalesItem,
  desactivarCatalogoCristalesItem,
  eliminarCatalogoCristalesItem,
  detectarDuplicadosCatalogoCristales,
} from '../api/backend_v2';
import { calcularPrecioEfectivoConDescuento, cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT, redondearMiles } from '../utils/precios';

const normalizar = (valor) => String(valor ?? '').toLowerCase().trim();

const formatearPrecio = (precio) => {
  if (!precio || precio === 0) return '$0';
  const numero = Math.round(parseFloat(precio));
  return `$${numero.toLocaleString('es-AR')}`;
};

const defaultCristalForm = (tipoVision = 'Monofocal') => ({
  tipo_vision: tipoVision,
  marca: '',
  marca_id: null,
  producto: '',
  producto_id: null,
  material: '',
  material_id: null,
  tratamiento: '',
  tratamiento_id: null,
  laboratorio: '',
  laboratorio_id: null,
  rango: '',
  indice: '',
  stock: true,
  antirreflejo: false,
  tiene_seguro: false,
  tiene_promocion: false,
  precio_publico: '',
  precio_efectivo: '',
});

const defaultLenteForm = {
  tipo_vision: '',
  marca: '',
  son_esteticas: false,
  color: '',
  rango: '',
  precio_publico: '',
  precio_efectivo: '',
};

const catalogoTipos = [
  { key: 'laboratorios', label: 'Laboratorios' },
  { key: 'productos_cristales', label: 'Productos cristales' },
  { key: 'materiales_cristales', label: 'Materiales cristales' },
  { key: 'tratamientos', label: 'Tratamientos' },
  { key: 'marcas_cristales', label: 'Marcas cristales' },
  { key: 'marcas_armazones', label: 'Marcas armazones' },
];

const CristalesHerramienta = () => {
  const [cristales, setCristales] = useState([]);
  const [lentesContacto, setLentesContacto] = useState([]);
  const [loading, setLoading] = useState(false);

  const [solapaActiva, setSolapaActiva] = useState('cristales');
  const [subSolapaActiva, setSubSolapaActiva] = useState('monofocales');
  const [busquedaCristales, setBusquedaCristales] = useState('');

  const [form, setForm] = useState(defaultCristalForm('Monofocal'));
  const [formLente, setFormLente] = useState({ ...defaultLenteForm });
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [precioEfectivoManual, setPrecioEfectivoManual] = useState(false);
  const [precioEfectivoManualLente, setPrecioEfectivoManualLente] = useState(false);

  const [editando, setEditando] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [tipoEditando, setTipoEditando] = useState('cristal');
  const [formEdicion, setFormEdicion] = useState(defaultCristalForm('Monofocal'));
  const [formEdicionLente, setFormEdicionLente] = useState({ ...defaultLenteForm });
  const [precioEfectivoManualEdicion, setPrecioEfectivoManualEdicion] = useState(false);
  const [precioEfectivoManualEdicionLente, setPrecioEfectivoManualEdicionLente] = useState(false);

  const [catalogos, setCatalogos] = useState({
    laboratorios: [],
    marcas_cristales: [],
    productos_cristales: [],
    materiales_cristales: [],
    tratamientos: [],
    marcas_armazones: [],
  });
  const [modalCatalogos, setModalCatalogos] = useState(false);
  const [catalogoActivo, setCatalogoActivo] = useState('productos_cristales');
  const [nuevoCatalogoNombre, setNuevoCatalogoNombre] = useState('');
  const [guardandoCatalogo, setGuardandoCatalogo] = useState(false);
  const [nuevoMaterialRango, setNuevoMaterialRango] = useState('');
  const [catalogoBusqueda, setCatalogoBusqueda] = useState('');
  const [catalogoEstadoFiltro, setCatalogoEstadoFiltro] = useState('todos');
  const [duplicadosCatalogo, setDuplicadosCatalogo] = useState([]);
  const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
  const [modoNuevoCatalogoCampo, setModoNuevoCatalogoCampo] = useState({
    cristalMarca: false,
    cristalProducto: false,
    cristalMaterial: false,
    cristalLaboratorio: false,
  });

  useEffect(() => {
    cargarDatos();

    cargarDescuentoEfectivoSeguro().then(setDescuentoEfectivo);
  }, []);

  useEffect(() => {
    if (solapaActiva !== 'cristales') return;
    let tipoVision = 'Monofocal';
    if (subSolapaActiva === 'bifocales') tipoVision = 'Bifocal';
    else if (subSolapaActiva === 'multifocales') tipoVision = 'Multifocal';
    else if (subSolapaActiva === 'ocupacionales') tipoVision = 'Ocupacional';
    setForm(defaultCristalForm(tipoVision));
    setPrecioEfectivoManual(false);
  }, [solapaActiva, subSolapaActiva]);

  const calcularPrecioEfectivo = (precioPublico) => {
    return calcularPrecioEfectivoConDescuento(precioPublico, descuentoEfectivo);
  };

  const cargarCatalogos = async () => {
    try {
      const res = await obtenerCatalogosCristales(false);
      setCatalogos({
        laboratorios: res.laboratorios || [],
        marcas_cristales: res.marcas_cristales || [],
        productos_cristales: res.productos_cristales || [],
        materiales_cristales: res.materiales_cristales || [],
        tratamientos: res.tratamientos || [],
        marcas_armazones: res.marcas_armazones || [],
      });
    } catch {
      // fallback silencioso cuando el entorno no tiene tablas normalizadas.
    }
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resCristales, resLentes] = await Promise.all([
        obtenerCristales(),
        obtenerLentesContacto(),
      ]);
      setCristales(resCristales.cristales || []);
      setLentesContacto(resLentes.lentes || []);
      await cargarCatalogos();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const cristalesFiltrados = useMemo(() => {
    if (solapaActiva !== 'cristales') return [];
    const busqueda = normalizar(busquedaCristales);
    return cristales.filter((cristal) => {
      const coincideTipo =
        (subSolapaActiva === 'monofocales' && cristal.tipo_vision === 'Monofocal') ||
        (subSolapaActiva === 'bifocales' && cristal.tipo_vision === 'Bifocal') ||
        (subSolapaActiva === 'multifocales' && cristal.tipo_vision === 'Multifocal') ||
        (subSolapaActiva === 'ocupacionales' && cristal.tipo_vision === 'Ocupacional') ||
        !subSolapaActiva;
      if (!coincideTipo) return false;
      if (!busqueda) return true;
      const campos = [
        cristal.tipo_vision,
        cristal.marca,
        cristal.laboratorio,
        cristal.producto,
        cristal.material,
        cristal.tratamiento,
        cristal.rango,
        cristal.indice,
        cristal.precio_publico,
        cristal.precio_efectivo,
        cristal.stock ? 'stock' : 'laboratorio',
        cristal.tiene_seguro ? 'seguro' : '',
        cristal.tiene_promocion ? 'promocion' : '',
      ].map(normalizar);
      return campos.some((valor) => valor.includes(busqueda));
    });

    return [...filtrados].sort((a, b) => {
      const cmpMarca = String(a.marca || '').localeCompare(String(b.marca || ''), 'es', { sensitivity: 'base' });
      if (cmpMarca !== 0) return cmpMarca;
      return String(a.producto || '').localeCompare(String(b.producto || ''), 'es', { sensitivity: 'base' });
    });
  }, [cristales, solapaActiva, subSolapaActiva, busquedaCristales]);

  const lentesFiltrados = useMemo(() => {
    if (solapaActiva !== 'lentes-contacto') return [];
    return [...lentesContacto].sort((a, b) => 
      String(a.marca || '').localeCompare(String(b.marca || ''), 'es', { sensitivity: 'base' })
    );
  }, [solapaActiva, lentesContacto]);

  const opcionesCatalogoActivas = useMemo(() => {
    const onlyActive = (arr) => (arr || []).filter((i) => i.activo).map((i) => i.nombre).filter(Boolean);
    return {
      laboratorios: onlyActive(catalogos.laboratorios),
      marcasCristales: onlyActive(catalogos.marcas_cristales),
      productosCristales: onlyActive(catalogos.productos_cristales),
      materialesCristales: onlyActive(catalogos.materiales_cristales),
      tratamientos: onlyActive(catalogos.tratamientos),
    };
  }, [catalogos]);

  const valorEnCatalogo = (lista, valor) => {
    const target = normalizar(valor);
    if (!target) return true;
    return (lista || []).some((item) => normalizar(item) === target);
  };

  const optionLegado = (listaActiva, valor) => {
    const limpio = String(valor ?? '').trim();
    if (!limpio) return null;
    if (valorEnCatalogo(listaActiva, limpio)) return null;
    return <option key={`legado-${limpio}`} value={limpio}>{`Valor legado: ${limpio}`}</option>;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setForm((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === 'precio_publico' && !precioEfectivoManual) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManual) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManual(String(value || '').trim() !== '');
    }
  };

  const handleChangeLente = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormLente((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === 'precio_publico' && !precioEfectivoManualLente) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManualLente) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManualLente(String(value || '').trim() !== '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const buscarIdPorNombre = (items, nombre) => {
        const found = (items || []).find((i) => normalizar(i.nombre) === normalizar(nombre));
        return found ? found.id : null;
      };

      const payload = {
        ...form,
        marca: String(form.marca || '').trim(),
        producto: String(form.producto || '').trim() || null,
        material: String(form.material || '').trim() || null,
        tratamiento: String(form.tratamiento || '').trim() || null,
        laboratorio: String(form.laboratorio || '').trim() || null,
        rango: String(form.rango || '').trim() || null,
        indice: form.indice !== '' ? parseFloat(form.indice) : null,
        precio_publico: form.precio_publico !== '' ? redondearMiles(parseFloat(form.precio_publico)) : 0,
        precio_efectivo: form.precio_efectivo !== '' ? redondearMiles(parseFloat(form.precio_efectivo)) : null,
        marca_id: form.marca_id || buscarIdPorNombre(catalogos.marcas_cristales, form.marca),
        producto_id: form.producto_id || buscarIdPorNombre(catalogos.productos_cristales, form.producto),
        material_id: form.material_id || buscarIdPorNombre(catalogos.materiales_cristales, form.material),
        tratamiento_id: form.tratamiento_id || buscarIdPorNombre(catalogos.tratamientos, form.tratamiento),
        laboratorio_id: form.laboratorio_id || buscarIdPorNombre(catalogos.laboratorios, form.laboratorio),
      };
      if (!payload.marca) throw new Error('La marca es obligatoria');

      await agregarCristal(payload);
      Swal.fire('Cristal agregado', '', 'success');
      setForm(defaultCristalForm(form.tipo_vision));
      setPrecioEfectivoManual(false);
      await cargarDatos();
    } catch (e2) {
      Swal.fire('Error', e2.message, 'error');
    }
  };

  const handleSubmitLente = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tipo_vision: formLente.tipo_vision || 'Monofocal',
        marca: String(formLente.marca || '').trim(),
        son_esteticas: formLente.son_esteticas,
        color: String(formLente.color || '').trim() || null,
        rango: String(formLente.rango || '').trim() || null,
        precio_publico: formLente.precio_publico !== '' ? redondearMiles(parseFloat(formLente.precio_publico)) : 0,
        precio_efectivo: formLente.precio_efectivo !== '' ? redondearMiles(parseFloat(formLente.precio_efectivo)) : null,
      };
      if (!payload.marca) throw new Error('La marca es obligatoria');
      await agregarLenteContacto(payload);
      Swal.fire('Lente de contacto agregado', '', 'success');
      setFormLente({ ...defaultLenteForm });
      setPrecioEfectivoManualLente(false);
      await cargarDatos();
    } catch (e2) {
      Swal.fire('Error', e2.message, 'error');
    }
  };

  const handleEditarCristal = (cristal) => {
    const resolverIdDesdeCatalogo = (items, nombre) => {
      const item = (items || []).find((it) => normalizar(it.nombre) === normalizar(nombre));
      return item ? item.id : null;
    };

    setItemEditando(cristal);
    setTipoEditando('cristal');
    setModoNuevoCatalogoCampo({
      cristalMarca: false,
      cristalProducto: false,
      cristalMaterial: false,
      cristalLaboratorio: false,
    });
    setFormEdicion({
      tipo_vision: cristal.tipo_vision || 'Monofocal',
      marca: cristal.marca || '',
      marca_id: cristal.marca_id ?? resolverIdDesdeCatalogo(catalogos.marcas_cristales, cristal.marca),
      producto: cristal.producto || '',
      producto_id: cristal.producto_id ?? resolverIdDesdeCatalogo(catalogos.productos_cristales, cristal.producto),
      material: cristal.material || '',
      material_id: cristal.material_id ?? resolverIdDesdeCatalogo(catalogos.materiales_cristales, cristal.material),
      tratamiento: cristal.tratamiento || '',
      tratamiento_id: cristal.tratamiento_id ?? resolverIdDesdeCatalogo(catalogos.tratamientos, cristal.tratamiento),
      laboratorio: cristal.laboratorio || '',
      laboratorio_id: cristal.laboratorio_id ?? resolverIdDesdeCatalogo(catalogos.laboratorios, cristal.laboratorio),
      rango: cristal.rango || '',
      indice: cristal.indice != null ? String(cristal.indice) : '',
      stock: cristal.stock ?? true,
      antirreflejo: cristal.antirreflejo ?? false,
      tiene_seguro: cristal.tiene_seguro ?? false,
      tiene_promocion: cristal.tiene_promocion ?? false,
      precio_publico: cristal.precio_publico != null ? String(cristal.precio_publico) : '',
      precio_efectivo: cristal.precio_efectivo != null ? String(cristal.precio_efectivo) : calcularPrecioEfectivo(cristal.precio_publico),
    });
    setPrecioEfectivoManualEdicion(cristal.precio_efectivo != null && String(cristal.precio_efectivo).trim() !== '');
    setEditando(true);
  };

  const handleEditarLente = (lente) => {
    setItemEditando(lente);
    setTipoEditando('lente');
    setFormEdicionLente({
      tipo_vision: lente.tipo_vision || 'Monofocal',
      marca: lente.marca || '',
      son_esteticas: lente.son_esteticas ?? false,
      color: lente.color || '',
      rango: lente.rango || '',
      precio_publico: lente.precio_publico != null ? String(lente.precio_publico) : '',
      precio_efectivo: lente.precio_efectivo != null ? String(lente.precio_efectivo) : calcularPrecioEfectivo(lente.precio_publico),
    });
    setPrecioEfectivoManualEdicionLente(lente.precio_efectivo != null && String(lente.precio_efectivo).trim() !== '');
    setEditando(true);
  };

  const handleEliminarCristal = async (cristal) => {
    const result = await Swal.fire({
      title: '¿Eliminar cristal?',
      text: `Se eliminará ${cristal.marca || 'el cristal'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;
    try {
      await eliminarCristal(cristal.id);
      Swal.fire('Eliminado', '', 'success');
      await cargarDatos();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleEliminarLente = async (lente) => {
    const result = await Swal.fire({
      title: '¿Eliminar lente de contacto?',
      text: `Se eliminará ${lente.marca || 'el lente'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;
    try {
      await eliminarLenteContacto(lente.id);
      Swal.fire('Eliminado', '', 'success');
      await cargarDatos();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleSubmitEdicion = async (e) => {
    e.preventDefault();
    try {
      const buscarIdPorNombre = (items, nombre) => {
        const found = (items || []).find((i) => normalizar(i.nombre) === normalizar(nombre));
        return found ? found.id : null;
      };

      const marcaId = formEdicion.marca_id || buscarIdPorNombre(catalogos.marcas_cristales, formEdicion.marca);
      const productoId = formEdicion.producto_id || buscarIdPorNombre(catalogos.productos_cristales, formEdicion.producto);
      const materialId = formEdicion.material_id || buscarIdPorNombre(catalogos.materiales_cristales, formEdicion.material);
      const tratamientoId = formEdicion.tratamiento_id || buscarIdPorNombre(catalogos.tratamientos, formEdicion.tratamiento);
      const laboratorioId = formEdicion.laboratorio_id || buscarIdPorNombre(catalogos.laboratorios, formEdicion.laboratorio);

      const payload = {
        ...formEdicion,
        marca: String(formEdicion.marca || '').trim(),
        marca_id: marcaId || null,
        producto: String(formEdicion.producto || '').trim() || null,
        producto_id: productoId || null,
        material: String(formEdicion.material || '').trim() || null,
        material_id: materialId || null,
        tratamiento: String(formEdicion.tratamiento || '').trim() || null,
        tratamiento_id: tratamientoId || null,
        laboratorio: String(formEdicion.laboratorio || '').trim() || null,
        laboratorio_id: laboratorioId || null,
        rango: String(formEdicion.rango || '').trim() || null,
        indice: formEdicion.indice !== '' ? parseFloat(formEdicion.indice) : null,
        precio_publico: formEdicion.precio_publico !== '' ? redondearMiles(parseFloat(formEdicion.precio_publico)) : 0,
        precio_efectivo: formEdicion.precio_efectivo !== '' ? redondearMiles(parseFloat(formEdicion.precio_efectivo)) : null,
      };
      if (!payload.marca) throw new Error('La marca es obligatoria');
      await editarCristal(itemEditando.id, payload);
      Swal.fire('Cristal actualizado', '', 'success');
      setEditando(false);
      setItemEditando(null);
      setPrecioEfectivoManualEdicion(false);
      await cargarDatos();
    } catch (e2) {
      Swal.fire('Error', e2.message, 'error');
    }
  };

  const handleSubmitEdicionLente = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tipo_vision: formEdicionLente.tipo_vision || 'Monofocal',
        marca: String(formEdicionLente.marca || '').trim(),
        son_esteticas: formEdicionLente.son_esteticas,
        color: String(formEdicionLente.color || '').trim() || null,
        rango: String(formEdicionLente.rango || '').trim() || null,
        precio_publico: formEdicionLente.precio_publico !== '' ? redondearMiles(parseFloat(formEdicionLente.precio_publico)) : 0,
        precio_efectivo: formEdicionLente.precio_efectivo !== '' ? redondearMiles(parseFloat(formEdicionLente.precio_efectivo)) : null,
      };
      if (!payload.marca) throw new Error('La marca es obligatoria');
      await editarLenteContacto(itemEditando.id, payload);
      Swal.fire('Lente de contacto actualizado', '', 'success');
      setEditando(false);
      setItemEditando(null);
      setPrecioEfectivoManualEdicionLente(false);
      await cargarDatos();
    } catch (e2) {
      Swal.fire('Error', e2.message, 'error');
    }
  };

  const handleChangeEdicion = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.endsWith('_id')) {
      const parsed = value === '' ? null : parseInt(value, 10);
      setFormEdicion((prev) => ({ ...prev, [name]: Number.isNaN(parsed) ? null : parsed }));
      return;
    }
    const nextValue = type === 'checkbox' ? checked : value;
    setFormEdicion((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === 'precio_publico' && !precioEfectivoManualEdicion) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManualEdicion) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManualEdicion(String(value || '').trim() !== '');
    }
  };

  const handleChangeEdicionLente = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormEdicionLente((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === 'precio_publico' && !precioEfectivoManualEdicionLente) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManualEdicionLente) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManualEdicionLente(String(value || '').trim() !== '');
    }
  };

  const cerrarModalEdicion = () => {
    setEditando(false);
    setItemEditando(null);
    setFormEdicion(defaultCristalForm('Monofocal'));
    setFormEdicionLente({ ...defaultLenteForm });
    setPrecioEfectivoManualEdicion(false);
    setPrecioEfectivoManualEdicionLente(false);
    setModoNuevoCatalogoCampo({
      cristalMarca: false,
      cristalProducto: false,
      cristalMaterial: false,
      cristalLaboratorio: false,
    });
  };

  const itemsCatalogoActivo = useMemo(() => {
    return (catalogos[catalogoActivo] || [])
      .filter((item) => {
        const nombre = String(item.nombre || '').toLowerCase();
        const busqueda = catalogoBusqueda.trim().toLowerCase();
        const matchTexto = !busqueda || nombre.includes(busqueda);
        const matchEstado =
          catalogoEstadoFiltro === 'todos'
            ? true
            : catalogoEstadoFiltro === 'activos'
              ? item.activo
              : !item.activo;
        return matchTexto && matchEstado;
      })
      .slice()
      .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
  }, [catalogos, catalogoActivo, catalogoBusqueda, catalogoEstadoFiltro]);

  const handleCrearCatalogo = async () => {
    const nombre = nuevoCatalogoNombre.trim();
    if (!nombre) return;
    setGuardandoCatalogo(true);
    try {
      const rango = catalogoActivo === 'materiales_cristales' ? (nuevoMaterialRango.trim() || null) : null;
      await crearCatalogoCristalesItem(catalogoActivo, nombre, null, null, rango);
      await cargarCatalogos();
      setNuevoCatalogoNombre('');
      setNuevoMaterialRango('');
      Swal.fire({ icon: 'success', title: 'Ítem guardado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setGuardandoCatalogo(false);
    }
  };

  const handleRenombrarCatalogo = async (item) => {
    const { value: nuevoNombre } = await Swal.fire({
      title: 'Renombrar ítem',
      input: 'text',
      inputValue: item.nombre,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!String(v || '').trim() ? 'Ingresá un nombre' : null),
    });
    if (!nuevoNombre) return;
    try {
      await actualizarCatalogoCristalesItem(catalogoActivo, item.id, { nombre: nuevoNombre.trim() });
      await cargarCatalogos();
      Swal.fire({ icon: 'success', title: 'Ítem actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleEditarMaterialDatos = async (item) => {
    const { value: datos } = await Swal.fire({
      title: 'Editar material',
      html: `
        <input id="swal-mat-nombre" class="swal2-input" placeholder="Nombre" value="${String(item.nombre || '').replace(/"/g, '&quot;')}">
        <input id="swal-mat-rango" class="swal2-input" placeholder="Rango" value="${String(item.rango || '').replace(/"/g, '&quot;')}">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nombre = document.getElementById('swal-mat-nombre')?.value?.trim() || '';
        const rango = document.getElementById('swal-mat-rango')?.value?.trim() || '';
        if (!nombre) {
          Swal.showValidationMessage('El nombre es obligatorio');
          return null;
        }
        return { nombre, rango };
      },
    });
    if (!datos) return;
    try {
      await actualizarCatalogoCristalesItem(catalogoActivo, item.id, datos);
      await cargarCatalogos();
      Swal.fire({ icon: 'success', title: 'Material actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleToggleActivoCatalogo = async (item) => {
    try {
      if (item.activo) await desactivarCatalogoCristalesItem(catalogoActivo, item.id);
      else await actualizarCatalogoCristalesItem(catalogoActivo, item.id, { activo: true });
      await cargarCatalogos();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleEliminarCatalogoItem = async (item) => {
    const result = await Swal.fire({
      title: 'Eliminar ítem',
      text: `Se eliminará definitivamente "${item.nombre}" del catálogo. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) return;

    try {
      await eliminarCatalogoCristalesItem(catalogoActivo, item.id);
      await cargarCatalogos();
      Swal.fire({ icon: 'success', title: 'Ítem eliminado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleDetectarDuplicados = async () => {
    setCargandoDuplicados(true);
    try {
      const res = await detectarDuplicadosCatalogoCristales(catalogoActivo);
      setDuplicadosCatalogo(res.grupos || []);
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setCargandoDuplicados(false);
    }
  };

  const leyendaCristales =
    subSolapaActiva === 'monofocales'
      ? 'Monofocales'
      : subSolapaActiva === 'bifocales'
        ? 'Bifocales'
        : subSolapaActiva === 'multifocales'
          ? 'Multifocales'
          : 'Ocupacionales';

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestión de Cristales y Lentes de Contacto</h2>
            <p className="text-sm text-gray-600">Alta, edición y administración de catálogos normalizados.</p>
          </div>
          <button
            onClick={() => {
              setCatalogoBusqueda('');
              setCatalogoEstadoFiltro('todos');
              setDuplicadosCatalogo([]);
              setNuevoMaterialRango('');
              setModalCatalogos(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <SwatchIcon className="h-4 w-4" /> Herramientas cristales
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
          <nav className="flex gap-1">
            <button
              onClick={() => setSolapaActiva('cristales')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-sm ${
                solapaActiva === 'cristales' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-blue-50'
              }`}
            >
              <BeakerIcon className="h-5 w-5" /> Cristales
            </button>
            <button
              onClick={() => setSolapaActiva('lentes-contacto')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-sm ${
                solapaActiva === 'lentes-contacto' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-green-50'
              }`}
            >
              <EyeIcon className="h-5 w-5" /> Lentes de Contacto
            </button>
          </nav>
        </div>

        {solapaActiva === 'cristales' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <nav className="flex flex-wrap gap-2">
              <button onClick={() => setSubSolapaActiva('monofocales')} className={`px-3 py-2 rounded-lg text-sm font-medium ${subSolapaActiva === 'monofocales' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-600'}`}><BeakerIcon className="h-4 w-4 inline mr-1" />Monofocales</button>
              <button onClick={() => setSubSolapaActiva('bifocales')} className={`px-3 py-2 rounded-lg text-sm font-medium ${subSolapaActiva === 'bifocales' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100 text-gray-600'}`}><AdjustmentsHorizontalIcon className="h-4 w-4 inline mr-1" />Bifocales</button>
              <button onClick={() => setSubSolapaActiva('multifocales')} className={`px-3 py-2 rounded-lg text-sm font-medium ${subSolapaActiva === 'multifocales' ? 'bg-emerald-100 text-emerald-700' : 'hover:bg-gray-100 text-gray-600'}`}><Squares2X2Icon className="h-4 w-4 inline mr-1" />Multifocales</button>
              <button onClick={() => setSubSolapaActiva('ocupacionales')} className={`px-3 py-2 rounded-lg text-sm font-medium ${subSolapaActiva === 'ocupacionales' ? 'bg-cyan-100 text-cyan-700' : 'hover:bg-gray-100 text-gray-600'}`}><BriefcaseIcon className="h-4 w-4 inline mr-1" />Ocupacionales</button>
            </nav>
          </div>
        )}

        {solapaActiva === 'cristales' ? (
          <form className="bg-white shadow rounded-lg p-6" onSubmit={handleSubmit}>
            <h3 className="text-lg font-semibold mb-4">Agregar Cristal {leyendaCristales}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de visión</label>
                <select name="tipo_vision" value={form.tipo_vision} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" required>
                  <option value="Monofocal">Monofocal</option>
                  <option value="Bifocal">Bifocal</option>
                  <option value="Multifocal">Multifocal</option>
                  <option value="Ocupacional">Ocupacional</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Marca</label>
                <input type="text" name="marca" list="catalogo-marcas-cristales-cht" value={form.marca} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Laboratorio</label>
                <input type="text" name="laboratorio" list="catalogo-laboratorios-cht" value={form.laboratorio} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Producto</label>
                <input type="text" name="producto" list="catalogo-productos-cristales-cht" value={form.producto} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Material</label>
                <input type="text" name="material" list="catalogo-materiales-cristales-cht" value={form.material} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tratamiento</label>
                <select name="tratamiento" value={form.tratamiento || ''} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md">
                  <option value="">Sin tratamiento</option>
                  {optionLegado(opcionesCatalogoActivas.tratamientos, form.tratamiento)}
                  {opcionesCatalogoActivas.tratamientos.map((nombre) => <option key={`trt-cht-${nombre}`} value={nombre}>{nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rango</label>
                <input type="text" name="rango" value={form.rango} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Ej: +2.00 a -4.00" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Índice</label>
                <input type="number" name="indice" value={form.indice} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" step="0.01" min="1.00" max="2.00" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precio lista</label>
                <input type="number" name="precio_publico" value={form.precio_publico} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                <p className="mt-1 text-xs text-gray-500">Efectivo auto con descuento {descuentoEfectivo}% (editable).</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precio efectivo</label>
                <input type="number" name="precio_efectivo" value={form.precio_efectivo} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="stock" checked={form.stock} onChange={handleChange} />Stock</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="tiene_seguro" checked={form.tiene_seguro} onChange={handleChange} />Seguro</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="tiene_promocion" checked={form.tiene_promocion} onChange={handleChange} />Promoción</label>
            </div>
            <div className="mt-5">
              <button type="submit" className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700"><PlusIcon className="h-4 w-4" />Agregar cristal</button>
            </div>
          </form>
        ) : (
          <form className="bg-white shadow rounded-lg p-6" onSubmit={handleSubmitLente}>
            <h3 className="text-lg font-semibold mb-4">Agregar Lente de Contacto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de visión</label>
                <input type="text" name="tipo_vision" value={formLente.tipo_vision} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Ej: Monofocal" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Marca</label>
                <input type="text" name="marca" value={formLente.marca} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <input type="text" name="color" value={formLente.color} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rango</label>
                <input type="text" name="rango" value={formLente.rango} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precio lista</label>
                <input type="number" name="precio_publico" value={formLente.precio_publico} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                <p className="mt-1 text-xs text-gray-500">Efectivo auto con descuento {descuentoEfectivo}% (editable).</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precio efectivo</label>
                <input type="number" name="precio_efectivo" value={formLente.precio_efectivo} onChange={handleChangeLente} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
              </div>
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="son_esteticas" checked={formLente.son_esteticas} onChange={handleChangeLente} />Son estéticas</label>
            </div>
            <div className="mt-5">
              <button type="submit" className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-md hover:bg-green-700"><PlusIcon className="h-4 w-4" />Agregar lente</button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">{solapaActiva === 'cristales' ? `Lista de Cristales ${leyendaCristales}` : 'Lista de Lentes de Contacto'}</h3>
            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">{solapaActiva === 'cristales' ? cristalesFiltrados.length : lentesFiltrados.length} registros</span>
          </div>

          {solapaActiva === 'cristales' && (
            <div className="px-4 pt-4 pb-2 bg-white border-b border-gray-100">
              <div className="relative max-w-xl">
                <input
                  type="text"
                  value={busquedaCristales}
                  onChange={(e) => setBusquedaCristales(e.target.value)}
                  placeholder="Buscar cristal por marca, producto, laboratorio, material, tratamiento, rango o índice"
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {busquedaCristales && (
                  <button
                    type="button"
                    onClick={() => setBusquedaCristales('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                    aria-label="Limpiar búsqueda"
                  >
                    ✖
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : solapaActiva === 'cristales' ? (
            cristalesFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No hay registros para esta categoría.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marca</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Laboratorio</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rango</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Indice</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lista</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efectivo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cristalesFiltrados.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-800">{c.tipo_vision}</td>
                        <td className="px-3 py-3 text-sm text-gray-800">{c.marca}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.laboratorio || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.producto || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.material || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.rango || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.indice ?? '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">{formatearPrecio(c.precio_publico)}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{c.precio_efectivo != null ? formatearPrecio(c.precio_efectivo) : '-'}</td>
                        <td className="px-3 py-3 text-xs">
                          <div className="flex flex-wrap gap-1">
                            <span className={`px-2 py-0.5 rounded-full ${c.stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.stock ? 'Stock' : 'Lab'}</span>
                            {c.tratamiento && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{c.tratamiento}</span>}
                            {!c.tratamiento && c.antirreflejo && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Antirreflejo</span>}
                            {c.tiene_seguro && <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Seguro</span>}
                            {c.tiene_promocion && <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Promo</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => handleEditarCristal(c)} className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center gap-1"><PencilIcon className="h-3 w-3" />Editar</button>
                            <button onClick={() => handleEliminarCristal(c)} className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 inline-flex items-center gap-1"><TrashIcon className="h-3 w-3" />Eliminar</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            lentesFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No hay lentes de contacto registrados.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marca</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rango</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estéticas</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lista</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efectivo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lentesFiltrados.map((l) => (
                      <tr key={l.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-800">{l.tipo_vision || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-800">{l.marca}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{l.color || '-'}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{l.rango || '-'}</td>
                        <td className="px-3 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs rounded-full ${l.son_esteticas ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'}`}>{l.son_esteticas ? 'Sí' : 'No'}</span>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900 font-medium">{formatearPrecio(l.precio_publico)}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{l.precio_efectivo != null ? formatearPrecio(l.precio_efectivo) : '-'}</td>
                        <td className="px-3 py-3 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => handleEditarLente(l)} className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 inline-flex items-center gap-1"><PencilIcon className="h-3 w-3" />Editar</button>
                            <button onClick={() => handleEliminarLente(l)} className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 inline-flex items-center gap-1"><TrashIcon className="h-3 w-3" />Eliminar</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>

      {editando && itemEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) cerrarModalEdicion(); }}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{tipoEditando === 'cristal' ? 'Editar cristal' : 'Editar lente de contacto'}</h3>
              <button onClick={cerrarModalEdicion} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            {tipoEditando === 'cristal' ? (
              <form onSubmit={handleSubmitEdicion} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo de visión</label>
                    <select name="tipo_vision" value={formEdicion.tipo_vision} onChange={handleChangeEdicion} className="w-full p-2 border border-gray-300 rounded-md" required>
                      <option value="Monofocal">Monofocal</option>
                      <option value="Bifocal">Bifocal</option>
                      <option value="Multifocal">Multifocal</option>
                      <option value="Ocupacional">Ocupacional</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Marca</label>
                    {!modoNuevoCatalogoCampo.cristalMarca ? (
                      <select
                        value={formEdicion.marca || ''}
                        onChange={(e) => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalMarca: true }));
                            setFormEdicion((prev) => ({ ...prev, marca: '', marca_id: null }));
                          } else {
                            const nombre = e.target.value;
                            const found = (catalogos.marcas_cristales || []).find((i) => i.nombre === nombre);
                            setFormEdicion((prev) => ({ ...prev, marca: nombre, marca_id: found ? found.id : null }));
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Seleccionar marca...</option>
                        {optionLegado(opcionesCatalogoActivas.marcasCristales, formEdicion.marca)}
                        {opcionesCatalogoActivas.marcasCristales.map((nombre) => <option key={`mcs-edit-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nueva marca...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="marca"
                          value={formEdicion.marca}
                          onChange={(e) => setFormEdicion((prev) => ({ ...prev, marca: e.target.value, marca_id: null }))}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Nueva marca"
                          required
                        />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalMarca: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Laboratorio</label>
                    {!modoNuevoCatalogoCampo.cristalLaboratorio ? (
                      <select
                        value={formEdicion.laboratorio || ''}
                        onChange={(e) => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalLaboratorio: true }));
                            setFormEdicion((prev) => ({ ...prev, laboratorio: '', laboratorio_id: null }));
                          } else {
                            const nombre = e.target.value;
                            const found = (catalogos.laboratorios || []).find((i) => i.nombre === nombre);
                            setFormEdicion((prev) => ({ ...prev, laboratorio: nombre, laboratorio_id: found ? found.id : null }));
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Seleccionar laboratorio...</option>
                        {optionLegado(opcionesCatalogoActivas.laboratorios, formEdicion.laboratorio)}
                        {opcionesCatalogoActivas.laboratorios.map((nombre) => <option key={`lab-edit-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo laboratorio...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="laboratorio"
                          value={formEdicion.laboratorio}
                          onChange={(e) => setFormEdicion((prev) => ({ ...prev, laboratorio: e.target.value, laboratorio_id: null }))}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Nuevo laboratorio"
                        />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalLaboratorio: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Producto</label>
                    {!modoNuevoCatalogoCampo.cristalProducto ? (
                      <select
                        value={formEdicion.producto || ''}
                        onChange={(e) => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalProducto: true }));
                            setFormEdicion((prev) => ({ ...prev, producto: '', producto_id: null }));
                          } else {
                            const nombre = e.target.value;
                            const found = (catalogos.productos_cristales || []).find((i) => i.nombre === nombre);
                            setFormEdicion((prev) => ({ ...prev, producto: nombre, producto_id: found ? found.id : null }));
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Seleccionar producto...</option>
                        {optionLegado(opcionesCatalogoActivas.productosCristales, formEdicion.producto)}
                        {opcionesCatalogoActivas.productosCristales.map((nombre) => <option key={`prod-edit-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo producto...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="producto"
                          value={formEdicion.producto}
                          onChange={(e) => setFormEdicion((prev) => ({ ...prev, producto: e.target.value, producto_id: null }))}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Nuevo producto"
                        />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalProducto: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Material</label>
                    {!modoNuevoCatalogoCampo.cristalMaterial ? (
                      <select
                        value={formEdicion.material || ''}
                        onChange={(e) => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalMaterial: true }));
                            setFormEdicion((prev) => ({ ...prev, material: '', material_id: null }));
                          } else {
                            const nombre = e.target.value;
                            const found = (catalogos.materiales_cristales || []).find((i) => i.nombre === nombre);
                            setFormEdicion((prev) => ({ ...prev, material: nombre, material_id: found ? found.id : null }));
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Seleccionar material...</option>
                        {optionLegado(opcionesCatalogoActivas.materialesCristales, formEdicion.material)}
                        {opcionesCatalogoActivas.materialesCristales.map((nombre) => <option key={`mat-edit-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo material...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="material"
                          value={formEdicion.material}
                          onChange={(e) => setFormEdicion((prev) => ({ ...prev, material: e.target.value, material_id: null }))}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Nuevo material"
                        />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, cristalMaterial: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tratamiento</label>
                    <select
                      value={formEdicion.tratamiento || ''}
                      onChange={(e) => {
                        const nombre = e.target.value;
                        const found = (catalogos.tratamientos || []).find((i) => i.nombre === nombre);
                        setFormEdicion((prev) => ({ ...prev, tratamiento: nombre, tratamiento_id: found ? found.id : null }));
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Sin tratamiento</option>
                      {optionLegado(opcionesCatalogoActivas.tratamientos, formEdicion.tratamiento)}
                      {opcionesCatalogoActivas.tratamientos.map((nombre) => <option key={`trt-edit-${nombre}`} value={nombre}>{nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Rango</label>
                    <input type="text" name="rango" value={formEdicion.rango} onChange={handleChangeEdicion} className="w-full p-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Índice</label>
                    <input type="number" name="indice" value={formEdicion.indice} onChange={handleChangeEdicion} className="w-full p-2 border border-gray-300 rounded-md" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio lista</label>
                    <input type="number" name="precio_publico" value={formEdicion.precio_publico} onChange={handleChangeEdicion} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                    <p className="mt-1 text-xs text-gray-500">Efectivo auto con descuento {descuentoEfectivo}% (editable).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio efectivo</label>
                    <input type="number" name="precio_efectivo" value={formEdicion.precio_efectivo} onChange={handleChangeEdicion} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="stock" checked={formEdicion.stock} onChange={handleChangeEdicion} />Stock</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="tiene_seguro" checked={formEdicion.tiene_seguro} onChange={handleChangeEdicion} />Seguro</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="tiene_promocion" checked={formEdicion.tiene_promocion} onChange={handleChangeEdicion} />Promoción</label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={cerrarModalEdicion} className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50">Cancelar</button>
                  <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Guardar cambios</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmitEdicionLente} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo de visión</label>
                    <input type="text" name="tipo_vision" value={formEdicionLente.tipo_vision} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Marca</label>
                    <input type="text" name="marca" value={formEdicionLente.marca} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Color</label>
                    <input type="text" name="color" value={formEdicionLente.color} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Rango</label>
                    <input type="text" name="rango" value={formEdicionLente.rango} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio lista</label>
                    <input type="number" name="precio_publico" value={formEdicionLente.precio_publico} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                    <p className="mt-1 text-xs text-gray-500">Efectivo auto con descuento {descuentoEfectivo}% (editable).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio efectivo</label>
                    <input type="number" name="precio_efectivo" value={formEdicionLente.precio_efectivo} onChange={handleChangeEdicionLente} className="w-full p-2 border border-gray-300 rounded-md" min="0" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="son_esteticas" checked={formEdicionLente.son_esteticas} onChange={handleChangeEdicionLente} />Son estéticas</label>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={cerrarModalEdicion} className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50">Cancelar</button>
                  <button type="submit" className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700">Guardar cambios</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {modalCatalogos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto z-50 px-4 pt-24 pb-6"
         onClick={(e) => {
          if (e.target === e.currentTarget) {
            setCatalogoBusqueda('');
            setCatalogoEstadoFiltro('todos');
            setNuevoMaterialRango('');
            setModalCatalogos(false);
          }
        }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[calc(100vh-7rem)] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BeakerIcon className="h-5 w-5 text-blue-600" />Herramientas de cristales: catálogos
              </h3>
              <button onClick={() => {
                setCatalogoBusqueda('');
                setCatalogoEstadoFiltro('todos');
                setNuevoMaterialRango('');
                setModalCatalogos(false);
              }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="flex flex-wrap gap-2">
                {catalogoTipos.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setCatalogoActivo(t.key)}
                    className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium ${
                      catalogoActivo === t.key
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : 'text-gray-600 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={nuevoCatalogoNombre}
                  onChange={(e) => setNuevoCatalogoNombre(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                  placeholder="Nuevo nombre"
                />
                <button
                  onClick={handleCrearCatalogo}
                  disabled={guardandoCatalogo || !nuevoCatalogoNombre.trim()}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {guardandoCatalogo ? 'Guardando...' : 'Agregar'}
                </button>
              </div>

              {catalogoActivo === 'materiales_cristales' && (
                <div className="grid grid-cols-1 gap-2">
                  <input
                    type="text"
                    value={nuevoMaterialRango}
                    onChange={(e) => setNuevoMaterialRango(e.target.value)}
                    placeholder="Rango del material"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={handleDetectarDuplicados}
                  disabled={cargandoDuplicados}
                  className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {cargandoDuplicados ? 'Detectando...' : 'Detectar duplicados'}
                </button>
                <button onClick={() => setDuplicadosCatalogo([])} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Limpiar resultado
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={catalogoBusqueda}
                  onChange={(e) => setCatalogoBusqueda(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                />
                <select
                  value={catalogoEstadoFiltro}
                  onChange={(e) => setCatalogoEstadoFiltro(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                >
                  <option value="todos">Todos</option>
                  <option value="activos">Sólo activos</option>
                  <option value="inactivos">Sólo inactivos</option>
                </select>
              </div>

              <div className="border border-gray-200 rounded-lg divide-y max-h-[380px] overflow-y-auto">
                {itemsCatalogoActivo.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">No hay ítems cargados para este catálogo.</p>
                )}
                {itemsCatalogoActivo.map((item) => (
                  <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.nombre}</p>
                      {catalogoActivo === 'materiales_cristales' && item.rango && (
                        <p className="text-xs text-gray-500">Rango: {item.rango}</p>
                      )}
                      <p className={`text-xs ${item.activo ? 'text-green-600' : 'text-gray-400'}`}>{item.activo ? 'Activo' : 'Inactivo'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {catalogoActivo === 'materiales_cristales' && (
                        <button onClick={() => handleEditarMaterialDatos(item)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Editar datos</button>
                      )}
                      <button onClick={() => handleRenombrarCatalogo(item)} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Renombrar</button>
                      <button onClick={() => handleToggleActivoCatalogo(item)} className={`px-2 py-1 text-xs rounded ${item.activo ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
                        {item.activo ? 'Desactivar' : 'Reactivar'}
                      </button>
                      <button onClick={() => handleEliminarCatalogoItem(item)} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {duplicadosCatalogo.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-amber-900">Duplicados detectados</h4>
                    <span className="text-xs text-amber-700">{duplicadosCatalogo.length} grupos</span>
                  </div>
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {duplicadosCatalogo.map((grupo) => (
                      <div key={grupo.nombre_normalizado} className="bg-white border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{grupo.nombre_normalizado}</p>
                            <p className="text-xs text-gray-500">{grupo.cantidad} registros · {grupo.activos} activos · {grupo.inactivos} inactivos</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {grupo.items.map((item) => (
                            <span key={item.id} className={`px-2 py-1 text-xs rounded-full border ${item.activo ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                              {item.nombre}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <datalist id="catalogo-laboratorios-cht">
        {opcionesCatalogoActivas.laboratorios.map((nombre) => <option key={`lab-cht-${nombre}`} value={nombre} />)}
      </datalist>
      <datalist id="catalogo-marcas-cristales-cht">
        {opcionesCatalogoActivas.marcasCristales.map((nombre) => <option key={`mc-cht-${nombre}`} value={nombre} />)}
      </datalist>
      <datalist id="catalogo-productos-cristales-cht">
        {opcionesCatalogoActivas.productosCristales.map((nombre) => <option key={`pc-cht-${nombre}`} value={nombre} />)}
      </datalist>
      <datalist id="catalogo-materiales-cristales-cht">
        {opcionesCatalogoActivas.materialesCristales.map((nombre) => <option key={`mt-cht-${nombre}`} value={nombre} />)}
      </datalist>
    </div>
  );
};

export default CristalesHerramienta;
